// src/domains/broadcast.ts —— 06 A-11「推广推送」原生域（Bot 广告推送，2026-10-21 拍板；切片 s.bc）：
// 进入页面先拉配额（enabled=false 整页「未开放」，不渲染任何编辑入口）；列表 / 编辑 / 详情（runs）/ run 详情（5s 轮询、失败明细分页签、无导出）。
// 代理端不展示层级、所属代理等平台信息（只展示本人 Bot 用户名）。文案预览只按白名单生成文本段，模板纯文本渲染（无 HTML 注入面）。
import type { Dict, Lang } from '../../../shared/src/dc';
import { interpolate } from '../../../shared/src/errors';
import { fmtDateTime, tzOffsetLabel } from '../../../shared/src/time';
import type { BcButton, BcCampaign, BcDelivery, BcRun, BcRunStatus, BcSchedule, BcScheduleKind, BcStatus } from '../broadcast-contract';
import { I18N_BROADCAST } from '../i18n/broadcast';
import { chipStyle, pagerStyle, tabStyle, type ChipKind } from '../ui';
import {
  askCancel,
  backToDetail,
  backToList,
  confirmGo,
  editCurrent,
  enter,
  estimate,
  lifecycle,
  loadDeliveries,
  loadList,
  loadRuns,
  newDraft,
  openDetail,
  openRun,
  pickImage,
  releaseBc,
  resetBc,
  saveDraft,
  sendPreview,
  submitCurrent,
  submitFromEditor,
} from './broadcast.actions';
import { KEYS, MODAL_KEYS } from './broadcast.keys';
import { FOOTER, MAX_BUTTONS, countWithFooter, parseRich, segStyle, textLimit } from './broadcast.lib';
import {
  DL_SIZE,
  DL_TABS,
  LIST_SIZE,
  RUNS_SIZE,
  bcOf,
  cancellable,
  editable,
  freshBc,
  runActive,
  setBc,
  setDraft,
  submittable,
  zhCount,
  type BcDraft,
  type BcSlice,
  type DlTab,
} from './broadcast.store';
import { str, type AgentCtx, type AgentDomain, type InputEvent } from './types';

const ST_KEY: Readonly<Record<BcStatus, readonly [string, ChipKind]>> = {
  draft: ['bcStDraft', 'gray'],
  pending_review: ['bcStPendingReview', 'amber'],
  rejected: ['bcStRejected', 'red'],
  scheduled: ['bcStScheduled', 'blue'],
  paused: ['bcStPaused', 'purple'],
  done: ['bcStDone', 'green'],
  cancelled: ['bcStCancelled', 'gray'],
};
const RS_KEY: Readonly<Record<BcRunStatus, readonly [string, ChipKind]>> = {
  queued: ['bcRsQueued', 'gray'],
  snapshotting: ['bcRsSnapshotting', 'blue'],
  sending: ['bcRsSending', 'blue'],
  paused: ['bcRsPaused', 'purple'],
  done: ['bcRsDone', 'green'],
  cancelled: ['bcRsCancelled', 'gray'],
  failed: ['bcRsFailed', 'red'],
};
const DS_KEY: Readonly<Record<BcDelivery['status'], string>> = { pending: 'bcDsPending', sending: 'bcDsSending', sent: 'bcDsSent', failed: 'bcDsFailed', skipped: 'bcDsSkipped' };
const FK_KEY: Readonly<Record<string, string>> = { blocked: 'bcFkBlocked', not_found: 'bcFkNotFound', rejected: 'bcFkRejected', bot_inactive: 'bcFkBotInactive', transient: 'bcFkTransient', partial: 'bcFkPartial' };
const DL_KEY: Readonly<Record<DlTab, string>> = { failed: 'bcDlFailed', blocked: 'bcDlBlocked', not_found: 'bcDlNotFound', skipped: 'bcDlSkipped' };
const STATUS_TABS: ReadonlyArray<'' | BcStatus> = ['', 'draft', 'pending_review', 'rejected', 'scheduled', 'paused', 'done', 'cancelled'];
const KINDS: ReadonlyArray<readonly [BcScheduleKind, string]> = [['now', 'bcSchNow'], ['once', 'bcSchOnce'], ['daily', 'bcSchDaily'], ['weekly', 'bcSchWeekly']];
const WEEKDAYS: readonly number[] = [1, 2, 3, 4, 5, 6, 7];
const CARD_OPTS: ReadonlyArray<readonly [string, string]> = [['any', 'bcCardAny'], ['has', 'bcCardHas'], ['none', 'bcCardNone']];
const LANG_OPTS: ReadonlyArray<readonly [string, string]> = [['any', 'bcLangAny'], ['zh', 'bcLangZh'], ['en', 'bcLangEn']];

// 模板静态文案（build split 双语守卫：模板 t.* 只直引原型 / 已登记扩展字典键，本域文案经 bc.tx 下发）
const TEXT_KEYS: readonly string[] = [
  'bcOffTitle', 'bcOffText', 'bcLoading', 'bcRetry', 'bcQuotaTitle', 'bcQuotaNoBot', 'bcNew', 'bcColTitle', 'bcColBots', 'bcColSchedule', 'bcColStatus',
  'bcColNext', 'bcColLast', 'bcEmpty', 'bcPrev', 'bcNext', 'bcBack', 'bcFTitle', 'bcFTitlePh', 'bcFBots', 'bcFBotsHint', 'bcFCard', 'bcFLang', 'bcFTextZh',
  'bcFTextEn', 'bcTagHint', 'bcFImage', 'bcImgPick', 'bcImgRemove', 'bcImgUploading', 'bcFButtons', 'bcBtnAdd', 'bcBtnRemove', 'bcBtnKindUrl',
  'bcBtnKindWebapp', 'bcBtnLabelZh', 'bcBtnLabelEn', 'bcBtnUrl', 'bcFSchedule', 'bcFRunAt', 'bcFTime', 'bcFWeekdays', 'bcFEndDate', 'bcEstimate',
  'bcPreviewTitle', 'bcPreviewEmpty', 'bcSaveDraft', 'bcSendPreview', 'bcDReject', 'bcDRejectHint', 'bcRuns', 'bcRunsEmpty', 'bcRColPlanned',
  'bcRColStatus', 'bcRColProgress', 'bcRColFailed', 'bcRColSkipped', 'bcRColFinished', 'bcBackDetail', 'bcRunPolling', 'bcRunTruncatedNote',
  'bcDlUser', 'bcDlBot', 'bcDlStatus', 'bcDlReason', 'bcDlTime', 'bcDlEmpty', 'bcCfBack',
];

const TOGGLE = (on: boolean): string =>
  `height:32px;padding:0 12px;border-radius:999px;border:1px solid ${on ? '#0d9a68' : '#dfe5e2'};background:${on ? '#e3f6ee' : '#fff'};color:${on ? '#0d9a68' : '#0f1a17'};font-weight:700;font-size:12px;cursor:pointer`;
const COUNT = (over: boolean): string => `font-size:11px;font-weight:600;color:${over ? '#d1434b' : '#8a958f'};text-align:right`;
const ACT = (kind: 'primary' | 'green' | 'danger' | 'plain', busy: boolean): string => {
  const bg = kind === 'primary' ? '#0f1a17' : kind === 'green' ? '#0d9a68' : '#fff';
  const fg = kind === 'primary' || kind === 'green' ? '#fff' : kind === 'danger' ? '#d1434b' : '#0f1a17';
  const bd = kind === 'danger' ? '#f3c2c5' : kind === 'plain' ? '#e6ebe8' : 'transparent';
  return `height:40px;padding:0 16px;border:1px solid ${bd};border-radius:10px;background:${bg};color:${fg};font-weight:700;font-size:13px;cursor:${busy ? 'wait' : 'pointer'};opacity:${busy ? 0.6 : 1}`;
};

function statusOf(t: Dict, st: BcStatus): { label: string; chip: string } {
  const [k, kind] = ST_KEY[st];
  return { label: str(t, k), chip: chipStyle(kind) };
}

function runStatusOf(t: Dict, st: BcRunStatus): { label: string; chip: string } {
  const [k, kind] = RS_KEY[st];
  return { label: str(t, k), chip: chipStyle(kind) };
}

export function scheduleText(t: Dict, sc: BcSchedule, tz: string): string {
  let base: string;
  if (sc.kind === 'now') base = str(t, 'bcSchNow');
  else if (sc.kind === 'once') base = interpolate(str(t, 'bcSchTextOnce'), { at: fmtDateTime(sc.run_at, { tz }) });
  else if (sc.kind === 'daily') base = interpolate(str(t, 'bcSchTextDaily'), { time: sc.time_of_day ?? '' });
  else {
    const days = [...(sc.weekdays ?? [])]
      .sort((a, b) => a - b)
      .map((n) => str(t, `bcWd${n}`))
      .join(str(t, 'bcWdSep'));
    base = interpolate(str(t, 'bcSchTextWeekly'), { days, time: sc.time_of_day ?? '' });
  }
  return sc.end_date && sc.kind !== 'now' && sc.kind !== 'once' ? base + interpolate(str(t, 'bcSchTextEnd'), { date: sc.end_date }) : base;
}

function tzLabel(t: Dict, tz: string): string {
  return interpolate(str(t, 'bcTz'), { tz: `${tz}（${tzOffsetLabel(tz)}）` });
}

function botsText(c: BcCampaign): string {
  return c.bots.map((b) => `@${b.username}`).join(' ');
}

function filterText(t: Dict, c: BcCampaign): string {
  const card = CARD_OPTS.find(([v]) => v === c.filter_card)?.[1] ?? 'bcCardAny';
  const lang = LANG_OPTS.find(([v]) => v === c.filter_lang)?.[1] ?? 'bcLangAny';
  return `${str(t, 'bcFCard')}：${str(t, card)} · ${str(t, 'bcFLang')}：${str(t, lang)}`;
}

function buttonsText(t: Dict, lang: Lang, buttons: readonly BcButton[]): string[] {
  return buttons.map((b) => (lang === 'en' && b.label_en.trim() ? b.label_en : b.label_zh));
}

// 白名单预览：英文文案为空时回落中文文案（与后端发送口径一致），尾注按收件语言
function previewOf(t: Dict, lang: Lang, textZh: string, textEn: string, buttons: readonly BcButton[], img: string) {
  const text = lang === 'en' && textEn.trim() ? textEn : textZh;
  const segs = parseRich(text).map((g) => ({ text: g.text, style: segStyle(g), isLink: g.href !== null, href: g.href ?? '' }));
  return { segs, empty: segs.length === 0, footer: FOOTER[lang], buttons: buttonsText(t, lang, buttons), hasButtons: buttons.length > 0, img, hasImg: !!img };
}

function pager(ctx: AgentCtx, page: number, total: number, size: number, go: (p: number) => void) {
  const pages = Math.max(1, Math.ceil(total / size));
  return {
    text: interpolate(str(ctx.t, 'bcTotal'), { n: total }),
    show: total > size,
    prev: () => (page > 1 ? go(page - 1) : undefined),
    next: () => (page < pages ? go(page + 1) : undefined),
    prevStyle: pagerStyle(page > 1),
    nextStyle: pagerStyle(page < pages),
    label: `${page} / ${pages}`,
  };
}

function quotaVals(ctx: AgentCtx, st: BcSlice) {
  const q = st.quota;
  const t = ctx.t;
  if (!q) return { rows: [], targets: '', periodic: '', noBots: false, notes: [] };
  const used = new Map(q.used_today.map((u) => [u.bot_id, u.used] as const));
  return {
    rows: st.bots.map((b) => {
      const n = used.get(b.id) ?? 0;
      return { label: interpolate(str(t, 'bcQuotaBot'), { bot: b.username, used: n, max: q.daily_per_bot }), style: chipStyle(n >= q.daily_per_bot ? 'red' : 'green') };
    }),
    targets: interpolate(str(t, 'bcQuotaTargets'), { n: q.max_targets }),
    periodic: interpolate(str(t, 'bcQuotaPeriodic'), { used: q.periodic_active, max: q.max_periodic }),
    noBots: st.bots.length === 0,
    notes: [str(t, q.review ? 'bcNoteReview' : 'bcNoteNoReview'), str(t, 'bcNoteAudience'), str(t, 'bcNoteFooter')],
  };
}

function listVals(ctx: AgentCtx, st: BcSlice) {
  const t = ctx.t;
  const tz = ctx.s.sh.config?.timezone;
  return {
    tabs: STATUS_TABS.map((v) => ({ label: v ? str(t, ST_KEY[v][0]) : str(t, 'all'), style: tabStyle(st.status === v), go: () => void loadList(ctx, 1, v) })),
    rows: st.list.map((c) => {
      const s = statusOf(t, c.status);
      return {
        title: c.title,
        bots: botsText(c),
        schedule: scheduleText(t, c.schedule, c.timezone || tz || 'UTC'),
        status: s.label,
        chip: s.chip,
        next: fmtDateTime(c.next_run_at, { tz: c.timezone || tz }),
        last: c.last_run ? `${c.last_run.sent} / ${c.last_run.target_total}` : '—',
        open: () => void openDetail(ctx, c.id),
      };
    }),
    empty: st.list.length === 0,
    pager: pager(ctx, st.page, st.total, LIST_SIZE, (p) => void loadList(ctx, p, st.status)),
    create: () => newDraft(ctx),
  };
}

function editVals(ctx: AgentCtx, st: BcSlice, d: BcDraft) {
  const t = ctx.t;
  const busy = ctx.s.saving;
  const max = textLimit(d.imageId !== null);
  const nZh = zhCount(d);
  const nEn = d.textEn.trim() ? countWithFooter(d.textEn, 'en') : 0;
  const img = d.imageId !== null ? st.thumbs[d.imageId] ?? '' : '';
  const setBtn = (i: number, patch: Partial<BcButton>): void => {
    const cur = bcOf(ctx.host.state).draft;
    if (!cur) return;
    setDraft(ctx, { buttons: cur.buttons.map((b, k) => (k === i ? { ...b, ...patch } : b)) }, { keepEstimate: true });
  };
  const est = st.estimate;
  return {
    heading: str(t, d.id === null ? 'bcEditNew' : 'bcEditTitle'),
    editHint: d.status === 'scheduled' ? str(t, 'bcDEditHint') : '',
    rejectNote: d.status === 'rejected' ? st.cur?.review_note ?? '' : '',
    title: d.title,
    onTitle: (e: InputEvent) => setDraft(ctx, { title: e.target.value.slice(0, 64) }, { keepEstimate: true }),
    bots: st.bots.map((b) => {
      const on = d.botIds.includes(b.id);
      return {
        label: `@${b.username}`,
        style: TOGGLE(on),
        toggle: () => {
          const cur = bcOf(ctx.host.state).draft;
          if (cur) setDraft(ctx, { botIds: cur.botIds.includes(b.id) ? cur.botIds.filter((x) => x !== b.id) : [...cur.botIds, b.id] });
        },
      };
    }),
    noBots: st.bots.length === 0,
    card: d.filterCard,
    onCard: (e: InputEvent) => {
      const v = e.target.value;
      if (v === 'any' || v === 'has' || v === 'none') setDraft(ctx, { filterCard: v });
    },
    cardOpts: CARD_OPTS.map(([v, k]) => ({ v, label: str(t, k) })),
    lang: d.filterLang,
    onLang: (e: InputEvent) => {
      const v = e.target.value;
      if (v === 'any' || v === 'zh' || v === 'en') setDraft(ctx, { filterLang: v });
    },
    langOpts: LANG_OPTS.map(([v, k]) => ({ v, label: str(t, k) })),
    textZh: d.textZh,
    onTextZh: (e: InputEvent) => setDraft(ctx, { textZh: e.target.value }, { keepEstimate: true }),
    countZh: interpolate(str(t, 'bcCount'), { n: nZh, max }),
    countZhStyle: COUNT(nZh > max),
    textEn: d.textEn,
    onTextEn: (e: InputEvent) => setDraft(ctx, { textEn: e.target.value }, { keepEstimate: true }),
    countEn: interpolate(str(t, 'bcCount'), { n: nEn, max }),
    countEnStyle: COUNT(nEn > max),
    hasImage: d.imageId !== null,
    img,
    hasImgSrc: !!img,
    uploading: st.uploading,
    onFile: (e: { target: { files?: ArrayLike<File> | null; value?: string } }) => void pickImage(ctx, e),
    removeImage: () => setDraft(ctx, { imageId: null }, { keepEstimate: true }),
    buttons: d.buttons.map((b, i) => ({
      n: i + 1,
      kind: b.kind,
      isUrl: b.kind === 'url',
      onKind: (e: InputEvent) => {
        const v = e.target.value;
        if (v === 'url' || v === 'webapp') setBtn(i, { kind: v });
      },
      labelZh: b.label_zh,
      onLabelZh: (e: InputEvent) => setBtn(i, { label_zh: e.target.value.slice(0, 64) }),
      labelEn: b.label_en,
      onLabelEn: (e: InputEvent) => setBtn(i, { label_en: e.target.value.slice(0, 64) }),
      url: b.url ?? '',
      onUrl: (e: InputEvent) => setBtn(i, { url: e.target.value.slice(0, 512) }),
      remove: () => {
        const cur = bcOf(ctx.host.state).draft;
        if (cur) setDraft(ctx, { buttons: cur.buttons.filter((_, k) => k !== i) }, { keepEstimate: true });
      },
    })),
    canAddBtn: d.buttons.length < MAX_BUTTONS,
    addBtn: () => {
      const cur = bcOf(ctx.host.state).draft;
      if (cur && cur.buttons.length < MAX_BUTTONS) setDraft(ctx, { buttons: [...cur.buttons, { kind: 'url', label_zh: '', label_en: '', url: '' }] }, { keepEstimate: true });
    },
    kinds: KINDS.map(([k, key]) => ({ label: str(t, key), style: TOGGLE(d.kind === k), go: () => setDraft(ctx, { kind: k }, { keepEstimate: true }) })),
    isOnce: d.kind === 'once',
    isPeriodic: d.kind === 'daily' || d.kind === 'weekly',
    isWeekly: d.kind === 'weekly',
    runAt: d.runAt,
    onRunAt: (e: InputEvent) => setDraft(ctx, { runAt: e.target.value }, { keepEstimate: true }),
    time: d.timeOfDay,
    onTime: (e: InputEvent) => setDraft(ctx, { timeOfDay: e.target.value.slice(0, 5) }, { keepEstimate: true }),
    weekdays: WEEKDAYS.map((n) => ({
      label: str(t, `bcWd${n}`),
      style: TOGGLE(d.weekdays.includes(n)),
      toggle: () => {
        const cur = bcOf(ctx.host.state).draft;
        if (cur) setDraft(ctx, { weekdays: cur.weekdays.includes(n) ? cur.weekdays.filter((x) => x !== n) : [...cur.weekdays, n] }, { keepEstimate: true });
      },
    })),
    endDate: d.endDate,
    onEndDate: (e: InputEvent) => setDraft(ctx, { endDate: e.target.value }, { keepEstimate: true }),
    tz: tzLabel(t, d.tz),
    est: {
      has: !!est,
      total: est ? interpolate(str(t, 'bcEstTotal'), { n: est.total }) : '',
      rows: est ? est.by_bot.map((r) => interpolate(str(t, 'bcEstBot'), { bot: r.username, n: r.count })) : [],
      capped: est?.capped ? interpolate(str(t, 'bcEstCapped'), { max: st.quota?.max_targets ?? '' }) : '',
    },
    doEstimate: () => void estimate(ctx),
    pvTabs: (['zh', 'en'] as const).map((l) => ({
      label: str(t, l === 'zh' ? 'bcPreviewZh' : 'bcPreviewEn'),
      style: TOGGLE(d.previewLang === l),
      go: () => {
        const cur = bcOf(ctx.host.state).draft;
        if (cur) setBc(ctx, { draft: { ...cur, previewLang: l } });
      },
    })),
    pv: previewOf(t, d.previewLang, d.textZh, d.textEn, d.buttons, img),
    err: st.formErr,
    busy,
    save: () => void saveDraft(ctx),
    saveStyle: ACT('plain', busy),
    preview: () => void sendPreview(ctx),
    previewStyle: ACT('plain', busy),
    submit: () => void submitFromEditor(ctx),
    submitStyle: ACT('green', busy),
    submitLabel: str(t, st.quota?.review ? 'bcSubmitReview' : 'bcSubmit'),
    back: () => (d.id === null ? backToList(ctx) : backToDetail(ctx)),
  };
}

function detailVals(ctx: AgentCtx, st: BcSlice, c: BcCampaign) {
  const t = ctx.t;
  const busy = ctx.s.saving;
  const tz = c.timezone || ctx.s.sh.config?.timezone || 'UTC';
  const s = statusOf(t, c.status);
  const img = c.image_file_id !== null ? st.thumbs[c.image_file_id] ?? '' : '';
  const actions: Array<{ label: string; style: string; run: () => void }> = [];
  if (editable(c.status)) actions.push({ label: str(t, c.status === 'rejected' ? 'bcAEditResubmit' : 'bcAEdit'), style: ACT('plain', busy), run: () => editCurrent(ctx) });
  if (submittable(c.status)) actions.push({ label: str(t, st.quota?.review ? 'bcSubmitReview' : 'bcSubmit'), style: ACT('green', busy), run: () => void submitCurrent(ctx) });
  if (c.status === 'scheduled') actions.push({ label: str(t, 'bcAPause'), style: ACT('plain', busy), run: () => void lifecycle(ctx, 'pause') });
  if (c.status === 'paused') actions.push({ label: str(t, 'bcAResume'), style: ACT('primary', busy), run: () => void lifecycle(ctx, 'resume') });
  if (cancellable(c.status)) actions.push({ label: str(t, 'bcACancel'), style: ACT('danger', busy), run: () => askCancel(ctx) });
  return {
    title: c.title,
    status: s.label,
    chip: s.chip,
    fields: [
      { k: str(t, 'bcDBots'), v: botsText(c) },
      { k: str(t, 'bcDFilter'), v: filterText(t, c) },
      { k: str(t, 'bcDSchedule'), v: `${scheduleText(t, c.schedule, tz)} · ${tzLabel(t, tz)}` },
      { k: str(t, 'bcDNext'), v: fmtDateTime(c.next_run_at, { tz }) },
      { k: str(t, 'bcDUpdated'), v: fmtDateTime(c.updated_at, { tz }) },
    ],
    hasReject: c.status === 'rejected' && !!c.review_note,
    rejectNote: c.review_note ?? '',
    editHint: c.status === 'scheduled' ? str(t, 'bcDEditHint') : '',
    pvZh: previewOf(t, 'zh', c.text_zh, c.text_en, c.buttons, img),
    pvEn: previewOf(t, 'en', c.text_zh, c.text_en, c.buttons, img),
    pvZhLabel: str(t, 'bcPreviewZh'),
    pvEnLabel: str(t, 'bcPreviewEn'),
    actions,
    runs: st.runs.map((r) => {
      const rs = runStatusOf(t, r.status);
      return {
        planned: fmtDateTime(r.planned_at, { tz }),
        status: rs.label,
        chip: rs.chip,
        progress: `${r.sent} / ${r.target_total}`,
        truncated: r.truncated ? str(t, 'bcTruncated') : '',
        failed: String(r.failed_blocked + r.failed_not_found + r.failed_other),
        skipped: String(r.skipped),
        finished: fmtDateTime(r.finished_at, { tz }),
        open: () => void openRun(ctx, r.id),
      };
    }),
    runsEmpty: st.runs.length === 0,
    runsPager: pager(ctx, st.runsPage, st.runsTotal, RUNS_SIZE, (p) => void loadRuns(ctx, c.id, p)),
    back: () => backToList(ctx),
  };
}

function runVals(ctx: AgentCtx, st: BcSlice, r: BcRun) {
  const t = ctx.t;
  const tz = st.cur?.timezone || ctx.s.sh.config?.timezone || 'UTC';
  const rs = runStatusOf(t, r.status);
  const failed = r.failed_blocked + r.failed_not_found + r.failed_other;
  const done = r.sent + failed + r.skipped;
  const pct = r.target_total > 0 ? Math.min(100, Math.round((done / r.target_total) * 100)) : runActive(r.status) ? 0 : 100;
  return {
    title: interpolate(str(t, 'bcRunTitle'), { id: r.id }),
    planned: fmtDateTime(r.planned_at, { tz }),
    status: rs.label,
    chip: rs.chip,
    polling: runActive(r.status),
    progress: interpolate(str(t, 'bcRunProgress'), { done, total: r.target_total }),
    barStyle: `height:100%;width:${pct}%;background:#0d9a68;border-radius:999px;transition:width .3s ease`,
    stats: [
      { label: str(t, 'bcRunSent'), value: String(r.sent) },
      { label: str(t, 'bcRunBlocked'), value: String(r.failed_blocked) },
      { label: str(t, 'bcRunNotFound'), value: String(r.failed_not_found) },
      { label: str(t, 'bcRunOther'), value: String(r.failed_other) },
      { label: str(t, 'bcRunSkipped'), value: String(r.skipped) },
    ],
    truncated: r.truncated,
    tabs: DL_TABS.map((k) => ({ label: str(t, DL_KEY[k]), style: tabStyle(st.dlTab === k), go: () => void loadDeliveries(ctx, r.id, k, 1) })),
    rows: st.dl.map((x) => ({
      user: x.user_no,
      bot: x.bot_username ? `@${x.bot_username}` : '—',
      status: str(t, DS_KEY[x.status]),
      reason: [x.fail_kind ? str(t, FK_KEY[x.fail_kind] ?? '') || x.fail_kind : '', x.fail_desc ?? ''].filter(Boolean).join(' · ') || '—',
      time: fmtDateTime(x.sent_at, { tz }),
    })),
    empty: st.dl.length === 0,
    pager: pager(ctx, st.dlPage, st.dlTotal, DL_SIZE, (p) => void loadDeliveries(ctx, r.id, st.dlTab, p)),
    back: () => backToDetail(ctx),
  };
}

export const broadcast: AgentDomain = {
  name: 'broadcast',
  keys: KEYS,
  modalKeys: MODAL_KEYS,
  migrated: true,
  i18n: I18N_BROADCAST,
  state: () => ({ bc: freshBc() }),
  mount(ctx) {
    // 会话结束即复位（停轮询、释放缩略图）：下一账号不得看到上一账号的活动
    ctx.events.on('session:lost', () => resetBc(ctx));
    // 每次进入本页重拉配额与列表；离开即停轮询并丢弃未保存草稿
    ctx.events.on('screen', ({ from, to }) => {
      if (to === 'broadcast') void enter(ctx);
      else if (from === 'broadcast') resetBc(ctx);
    });
  },
  unmount(ctx) {
    releaseBc(ctx);
  },
  vals(ctx) {
    const { t, s } = ctx;
    const st = bcOf(s);
    const q = st.quota;
    const on = !!q && q.enabled;
    const cf = st.confirm;
    const busy = s.saving;
    return {
      bc: {
        tx: Object.fromEntries(TEXT_KEYS.map((k) => [k, str(t, k)])),
        loading: !q && !st.loadErr,
        loadErr: st.loadErr,
        retry: () => void enter(ctx),
        off: !!q && !q.enabled,
        on,
        quota: quotaVals(ctx, st),
        isList: on && st.view === 'list',
        isEdit: on && st.view === 'edit' && !!st.draft,
        isDetail: on && st.view === 'detail' && !!st.cur,
        isRun: on && st.view === 'run' && !!st.run,
        list: listVals(ctx, st),
        ed: on && st.draft ? editVals(ctx, st, st.draft) : null,
        dt: on && st.cur ? detailVals(ctx, st, st.cur) : null,
        rn: on && st.run ? runVals(ctx, st, st.run) : null,
        cf: {
          open: on && !!cf,
          title: str(t, cf?.kind === 'cancel' ? 'bcCfCancelTitle' : 'bcCfSubmitTitle'),
          lines: cf?.lines ?? [],
          okLabel: str(t, cf?.kind === 'cancel' ? 'bcCfCancelOk' : 'bcCfSubmitOk'),
          okStyle: `flex:1;height:44px;border:0;border-radius:11px;background:${cf?.kind === 'cancel' ? '#d1434b' : '#0d9a68'};color:#fff;font-weight:700;cursor:pointer;opacity:${busy ? 0.6 : 1}`,
          ok: () => void confirmGo(ctx),
          close: () => setBc(ctx, { confirm: null }),
        },
      },
    };
  },
};
